import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Skeleton, SkeletonCircle, SkeletonRegion } from '../../../components/ui/Skeleton';
import './CourseOutlineTree.css';

/**
 * CourseOutlineTree
 * ------------------
 * The collapsible module/section list used by both the desktop outline rail
 * and the mobile outline drawer. Previously this was copy-pasted in
 * CourseLearnView (desktop ~:527-732, mobile ~:364-489) and had already
 * drifted (font sizes, gaps, dot sizes, radii). This is now the single
 * source of truth for both surfaces.
 *
 * TERMINOLOGY: the outline nests `lessons[].sections[]`. An outline
 * "lesson" is a MODULE; an outline "section" is a single readable unit
 * (what the UI calls a "lesson"). See useCourseProgress.js for the same
 * note — kept consistent here.
 *
 * MODULE EXPANSION: only the module containing the selected section is
 * expanded by default; everything else collapses to a single row with a
 * count ("5 · quiz"). Manual toggles win over that default and stick until
 * the reader moves into a different module.
 *
 * Row state precedence (highest wins): current (selected) > completed >
 * quiz/knowledge-check > upcoming. "Current" wins over "completed" so that
 * revisiting an already-finished section still shows "you are here".
 *
 * There is no locked/gated concept in this product and no duration data —
 * do not add either here (see task notes in CourseLearnView.jsx).
 */

function isSectionComplete(section, localViewed) {
  if (!section) return false;
  if (section.viewed === true || section.passed === true) return true;
  if (localViewed && typeof localViewed.has === 'function') {
    return localViewed.has(section.sectionId);
  }
  return false;
}

function isQuizLike(section) {
  const ct = String(section?.contentType || '').toUpperCase();
  return ct === 'QUIZ' || ct === 'KNOWLEDGE_CHECK';
}

function getRowState(section, { active, localViewed }) {
  if (active) return 'current';
  if (isSectionComplete(section, localViewed)) return 'completed';
  if (isQuizLike(section)) return 'quiz';
  return 'upcoming';
}

function SectionStateIcon({ state }) {
  if (state === 'completed') {
    return (
      <svg className="outline-row-icon" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle className="outline-icon-completed-fill" cx="8" cy="8" r="7" />
        <path className="outline-icon-completed-check" d="M4.8 8.2l2.1 2.1 4.3-4.4" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    );
  }
  if (state === 'current') {
    return (
      <svg className="outline-row-icon" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle className="outline-icon-current-ring" cx="8" cy="8" r="6.4" strokeWidth="1.6" fill="none" />
        <circle className="outline-icon-current-dot" cx="8" cy="8" r="2.6" />
      </svg>
    );
  }
  if (state === 'quiz') {
    return (
      <svg className="outline-row-icon" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect className="outline-icon-quiz" x="2.4" y="2.4" width="11.2" height="11.2" rx="3" strokeWidth="1.6" fill="none" />
        <path className="outline-icon-quiz" d="M5.6 8h4.8M5.6 5.4h4.8M5.6 10.6h2.6" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  // upcoming
  return (
    <svg className="outline-row-icon" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle className="outline-icon-upcoming-ring" cx="8" cy="8" r="6.4" strokeWidth="1.6" fill="none" />
    </svg>
  );
}

const STATE_LABEL = {
  completed: 'Completed',
  current: 'Current lesson',
  quiz: 'Quiz, not yet completed',
  upcoming: 'Not yet completed',
};

/**
 * @param {Object} props
 * @param {Object|null} props.outline - course outline ({ lessons: [{ lessonId, order, title, sections: [...] }] })
 * @param {boolean} props.loadingOutline
 * @param {string|null} props.selectedSectionId
 * @param {Set<string>} [props.localViewed] - optimistic set of viewed sectionIds
 * @param {string|null} [props.currentLessonId] - lessonId of the currently loaded lesson (passed through to onSectionClick)
 * @param {(lessonId: string, sectionId: string, currentLessonId: string|null) => void} props.onSectionClick
 * @param {React.MutableRefObject<Object>} [props.sectionButtonRefs] - ref object to populate with { [sectionId]: buttonEl }.
 *   If omitted, an internal ref is used (the mobile drawer doesn't need scroll restoration).
 */
export default function CourseOutlineTree({
  outline,
  loadingOutline,
  selectedSectionId,
  localViewed,
  currentLessonId,
  onSectionClick,
  sectionButtonRefs: externalRefs,
}) {
  const internalRefs = useRef({});
  const sectionButtonRefs = externalRefs || internalRefs;

  const modules = useMemo(
    () => (Array.isArray(outline?.lessons) ? outline.lessons : []),
    [outline]
  );

  // The module holding the current section — the only one the mockup shows
  // expanded (every other module is a single collapsed row, lines 582-601).
  const activeModuleId = useMemo(() => {
    if (!selectedSectionId) return null;
    const owner = modules.find((mod) =>
      (Array.isArray(mod?.sections) ? mod.sections : []).some((sec) => sec?.sectionId === selectedSectionId)
    );
    return owner ? owner.lessonId : null;
  }, [modules, selectedSectionId]);

  // Manual toggles keyed by moduleId (true = user expanded, false = user
  // collapsed). Modules with no entry fall back to "expanded iff active", so
  // the rail follows the reader by default without overriding a deliberate
  // collapse of the module being read.
  const [overrides, setOverrides] = useState(() => ({}));

  // Navigating INTO a module drops its stale override so the section that was
  // just selected can never be hidden behind a collapsed header. Adjusting
  // state during render (rather than in an effect) avoids a frame where the
  // new current module paints collapsed.
  const prevActiveModuleId = useRef(activeModuleId);
  if (prevActiveModuleId.current !== activeModuleId) {
    prevActiveModuleId.current = activeModuleId;
    if (activeModuleId && overrides[activeModuleId] !== undefined) {
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[activeModuleId];
        return next;
      });
    }
  }

  const toggleModule = useCallback((moduleId, expanded) => {
    setOverrides((prev) => ({ ...prev, [moduleId]: !expanded }));
  }, []);

  if (loadingOutline) {
    // One expanded module over a run of collapsed ones — the shape the tree
    // settles into, so the rail doesn't reshuffle when the outline lands.
    return (
      <SkeletonRegion label="Loading outline" className="outline-tree">
        <div style={{ padding: '8px', marginBottom: 2 }}>
          <Skeleton width="82%" height={13} />
        </div>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 9px' }}>
            <SkeletonCircle size={15} />
            <Skeleton height={12} style={{ flex: 1, maxWidth: `${88 - i * 9}%` }} />
          </div>
        ))}
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} style={{ padding: '8px', marginTop: i === 0 ? 10 : 0 }}>
            <Skeleton width={`${76 - i * 6}%`} height={13} />
          </div>
        ))}
      </SkeletonRegion>
    );
  }

  return (
    <div className="outline-tree">
      {modules.map((mod) => {
        const moduleId = mod.lessonId;
        const expanded = overrides[moduleId] ?? moduleId === activeModuleId;
        const collapsed = !expanded;
        const sections = Array.isArray(mod.sections) ? mod.sections : [];
        const hasQuiz = sections.some(isQuizLike);

        return (
          <div className="outline-module" key={moduleId}>
            <button
              type="button"
              className="outline-module-header"
              onClick={() => toggleModule(moduleId, expanded)}
              aria-expanded={!collapsed}
              aria-controls={`outline-module-sections-${moduleId}`}
            >
              <svg
                className={`outline-module-chevron${collapsed ? ' is-collapsed' : ''}`}
                width="11"
                height="11"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path d="M2.6 4.4L6 7.8l3.4-3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="outline-module-title">{mod.order}. {mod.title}</span>
              {collapsed && (
                <span className="outline-module-meta">
                  {sections.length}{hasQuiz ? ' · quiz' : ''}
                </span>
              )}
            </button>

            {!collapsed && (
              <div className="outline-section-list" id={`outline-module-sections-${moduleId}`}>
                {sections.map((sec) => {
                  const active = sec.sectionId === selectedSectionId;
                  const state = getRowState(sec, { active, localViewed });

                  return (
                    <button
                      type="button"
                      key={sec.sectionId}
                      ref={(el) => { sectionButtonRefs.current[sec.sectionId] = el; }}
                      className={`outline-row is-${state}`}
                      aria-current={active ? 'true' : undefined}
                      title={`${sec.title} — ${STATE_LABEL[state]}`}
                      onClick={() => onSectionClick(moduleId, sec.sectionId, currentLessonId)}
                    >
                      <SectionStateIcon state={state} />
                      <span className="outline-row-title">{sec.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
